import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Checkbox, Box, Text, Stack, useResponsiveValue, Columns, Column } from "braid-design-system";
import { BadgeWithTooltip } from "../BadgeWithTooltip/index.mjs";
import { translations } from "../../../../private/components/TeamMemberAccessSection/.vocab/index.mjs";
import { TeamMemberInfoRow } from "../TeamMemberInfoRow/index.mjs";
import { translations as translations$1 } from "../../../../private/components/TeamMemberNotifySection/.vocab/index.mjs";
import { translations as translations$2 } from "../../../../private/components/TeamMemberJobAccess/.vocab/index.mjs";
import { oddRow } from "./index.css.mjs";
const TeamMemberAccessSection = ({
  teamMember: {
    hasAccessToAllJobs,
    userId,
    firstName,
    lastName,
    isPending,
    accessSelected,
    isJobCreator
  },
  id,
  onSelectTeamMemberJobAccess
}) => {
  const { t } = useTranslations(translations);
  if (isPending) {
    return /* @__PURE__ */ jsx(
      BadgeWithTooltip,
      {
        id,
        badgeText: t("Invite Pending"),
        badgeTone: "neutral",
        tooltipText: t("User has not yet activated their account.")
      }
    );
  }
  const handleAccessSelect = (event) => {
    onSelectTeamMemberJobAccess(userId, event.currentTarget.checked);
  };
  return hasAccessToAllJobs ? /* @__PURE__ */ jsx(
    BadgeWithTooltip,
    {
      id,
      badgeText: t("Has Access"),
      badgeTone: "info",
      tooltipText: t(
        "User has access to all jobs. Permissions can only be changed in user management."
      )
    }
  ) : /* @__PURE__ */ jsx(
    Checkbox,
    {
      id: `${id}-job-access-checkbox-${userId}`,
      label: "",
      "aria-label": t("Select to give job access to name", {
        firstName,
        lastName
      }),
      checked: accessSelected,
      onChange: handleAccessSelect,
      disabled: isJobCreator
    }
  );
};
const TeamMemberNotifySection = ({
  teamMember: {
    userId,
    isPending,
    notifySelected,
    firstName,
    lastName,
    isJobCreator
  },
  id,
  onSelectTeamMemberNotify
}) => {
  const { t } = useTranslations(translations$1);
  const handleNotifySelect = (event) => {
    onSelectTeamMemberNotify(userId, event.currentTarget.checked);
  };
  return isPending || isJobCreator ? null : /* @__PURE__ */ jsx(
    Checkbox,
    {
      id: `${id}-job-notify-checkbox-${userId}`,
      label: "",
      "aria-label": t("Select to notify", { firstName, lastName }),
      checked: notifySelected,
      onChange: handleNotifySelect
    }
  );
};
const TruncatedText = ({
  text,
  isSecondary = false
}) => /* @__PURE__ */ jsx(Box, { title: text, "data-hj-suppress": true, children: /* @__PURE__ */ jsx(
  Text,
  {
    baseline: false,
    maxLines: 1,
    tone: isSecondary ? "secondary" : "neutral",
    children: text
  }
) });
const TeamMemberJobAccess = ({
  id,
  teamMember,
  onSelectTeamMemberJobAccess,
  onSelectTeamMemberNotify,
  isOddRow
}) => {
  const { t } = useTranslations(translations$2);
  const { firstName, lastName, email, isJobCreator } = teamMember;
  const nameSection = /* @__PURE__ */ jsx(
    TruncatedText,
    {
      text: t("name section", {
        firstName,
        lastName,
        isJobCreator: isJobCreator ? "true" : "other"
      }),
      isSecondary: isJobCreator
    }
  );
  const emailSection = /* @__PURE__ */ jsx(TruncatedText, { text: email, isSecondary: isJobCreator });
  const accessSection = /* @__PURE__ */ jsx(
    TeamMemberAccessSection,
    {
      id,
      teamMember,
      onSelectTeamMemberJobAccess
    }
  );
  const notifySection = /* @__PURE__ */ jsx(
    TeamMemberNotifySection,
    {
      id,
      teamMember,
      onSelectTeamMemberNotify
    }
  );
  const defaultLayout = /* @__PURE__ */ jsx(Box, { paddingX: "small", children: /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
    /* @__PURE__ */ jsx(Column, { width: "1/4", children: nameSection }),
    /* @__PURE__ */ jsx(Column, { width: "1/3", children: emailSection }),
    /* @__PURE__ */ jsx(Column, { width: "1/5", children: accessSection }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: notifySection })
  ] }) });
  const mobileLayout = /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(TeamMemberInfoRow, { title: t("Name"), children: nameSection }),
    /* @__PURE__ */ jsx(TeamMemberInfoRow, { title: t("Email"), children: emailSection }),
    /* @__PURE__ */ jsx(TeamMemberInfoRow, { title: t("Access"), children: accessSection }),
    /* @__PURE__ */ jsx(TeamMemberInfoRow, { title: t("Send Invite"), children: notifySection })
  ] });
  const showMobileLayout = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  return /* @__PURE__ */ jsx(Box, { className: { [oddRow]: isOddRow }, paddingY: "medium", children: showMobileLayout ? mobileLayout : defaultLayout });
};
export {
  TeamMemberJobAccess
};
